import { useRef, useEffect, useCallback } from 'react';
import {
  isOptionSelected,
  getLocationString,
  shouldShowLocation,
  getOptionIndex,
  getDisplayData,
  shouldShowDocId,
} from '../../AttorneyModal/helpers';

/**
 * Normalizes selectedMembers to always be an array.
 * When multiple={false}, selectedMembers might be a single object or null.
 * @param {Array|Object|null|undefined} selectedMembers - The selected members to normalize
 * @returns {Array} - Normalized array of selected members
 */
const normalizeSelectedMembers = (selectedMembers) => {
  if (Array.isArray(selectedMembers)) {
    return selectedMembers;
  }
  if (selectedMembers != null) {
    return [selectedMembers];
  }
  return [];
};

/**
 * Creates popper configuration props for the Autocomplete component.
 * @returns {Object} - Popper configuration object
 */
const createPopperProps = () => ({
  placement: 'bottom-start',
  disablePortal: false,
  modifiers: {
    preventOverflow: {
      enabled: true,
      boundariesElement: 'viewport',
    },
    flip: {
      enabled: false,
    },
  },
});

/**
 * Builds option props object for rendering an autocomplete option.
 * @param {Object} option - The option to build props for
 * @param {Array} normalizedSelectedMembers - Normalized array of selected members
 * @param {Array} currentOptions - Current list of all options
 * @param {boolean} showDisplayNamesOnly - Whether to show display names only
 * @returns {Object} - Option props object
 */
const buildOptionProps = (option, normalizedSelectedMembers, currentOptions, showDisplayNamesOnly) => {
  const optionSelected = isOptionSelected(option, normalizedSelectedMembers);
  const locationString = getLocationString(option);
  const showLocation = shouldShowLocation(locationString);
  const optionIndex = getOptionIndex(option, currentOptions);
  const isFirstOption = optionIndex === 0;
  const { displayName, displayRole } = getDisplayData(option);
  const showDocId = shouldShowDocId(option);

  return {
    option,
    isSelected: optionSelected,
    isFirstOption,
    displayName,
    displayRole,
    locationString,
    shouldShowDocId: showDocId,
    shouldShowLocation: showLocation,
    showDisplayNamesOnly,
  };
};

export const useMemberAutocomplete = ({
  memberOptions = [],
  selectedMembers = [],
  onMembersChange,
  onSearchChange,
  showDisplayNamesOnly = false,
}) => {
  // Store memberOptions in a ref to access in renderOption
  const memberOptionsRef = useRef(memberOptions);
  
  useEffect(() => {
    memberOptionsRef.current = memberOptions;
  }, [memberOptions]);

  // Handle option change
  const handleChange = useCallback(
    (_, newValue, reason) => {
      // MUI Autocomplete deletes the last selected value on Backspace when the
      // input is empty (reason: remove-option). Selections are owned by external
      // chips/cards (renderTags is null), so ignore keyboard removals here.
      if (reason === 'remove-option') {
        return;
      }
      onMembersChange(newValue);
    },
    [onMembersChange]
  );

  // Handle input change
  const handleInputChange = useCallback(
    (_, newInputValue, reason) => {
      if (reason === 'input') {
        onSearchChange(newInputValue);
      }
    },
    [onSearchChange]
  );

  /**
   * Input keydown handler. @material-ui/lab Autocomplete attaches Backspace
   * removal on the root (getRootProps), not inputProps — stopPropagation so
   * empty-input Backspace does not bubble up and delete selections.
   * @param {KeyboardEvent} event
   * @param {(event: KeyboardEvent) => void} [autocompleteKeyDown]
   */
  const handleKeyDown = useCallback((event, autocompleteKeyDown) => {
    if (event.key === 'Enter') {
      event.preventDefault();
    }

    if (event.key === 'Backspace' && !event.target.value) {
      event.stopPropagation();
      return;
    }

    autocompleteKeyDown?.(event);
  }, []);

  // Get option props for rendering
  const getOptionProps = useCallback(
    (option) => {
      const currentOptions = memberOptionsRef.current;
      const normalizedSelectedMembers = normalizeSelectedMembers(selectedMembers);
      return buildOptionProps(option, normalizedSelectedMembers, currentOptions, showDisplayNamesOnly);
    },
    [selectedMembers, showDisplayNamesOnly]
  );

  // Popper props configuration
  const popperProps = createPopperProps();

  return {
    memberOptionsRef,
    handleChange,
    handleInputChange,
    handleKeyDown,
    getOptionProps,
    popperProps,
  };
};
